"use client";

import { useEffect, useState, useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faBookOpen,
  faUsers,
  faFileAlt,
  faTags,
  faBoxOpen,
  faMoneyBillWave,
  faShoppingCart,
  faSignInAlt,
  faCalendar,
} from "@fortawesome/free-solid-svg-icons";
import loaderContext from "../../context/LoaderContext";
import axios from "axios";
import { baseUrl } from "../../config/baseUrl";

export default function Dashboard() {
  const { setLoader } = useContext(loaderContext);
  const [dashboardCounts, setDashboardCounts] = useState({});
  const [chartData, setChartData] = useState({});

  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState("");

  const fetchDashboardCounts = async (start = null, end = null) => {
    try {
      setLoader(true);
      const url =
        start && end
          ? `${baseUrl}/admin/dashboard?startDate=${start}&endDate=${end}`
          : `${baseUrl}/admin/dashboard`;

      const response = await axios.get(url);
      const data = response?.data.data;

      setDashboardCounts(data);
      setChartData(data.chart || {});
    } catch (error) {
      console.error("Error fetching dashboard counts:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchDashboardCounts(startDate, endDate);
  }, []);

  const handleDateFilter = () => {
    fetchDashboardCounts(startDate, endDate);
  };

  const handleResetToToday = () => {
    setStartDate(today);
    setEndDate(today);
    fetchDashboardCounts(today, today);
  };
  const handleResetToDefault = () => {
    setStartDate("");
    setEndDate("");
    fetchDashboardCounts("", "");
  };

  // --- Doughnut Chart Calculation ---
  const libraryRevenue = chartData.libraryRevenueResult || 0;
  const ebookRevenue = chartData.ebookRevenueResult || 0;
  const packageRevenue = chartData.packageRevenueResult || 0;

  const totalRevenue = libraryRevenue + ebookRevenue + packageRevenue;
  const otherRevenue = ebookRevenue + packageRevenue;

  const formatCurrency = (value) =>
    `₹${Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    })}`;

  const getPercentage = (value) =>
    totalRevenue > 0 ? (value / totalRevenue) * 100 : 0;

  const libraryPercent = getPercentage(libraryRevenue);
  const ebookPercent = getPercentage(ebookRevenue);
  const packagePercent = getPercentage(packageRevenue);

  const pieChartStyle = {
    background: `conic-gradient(
      var(--color-library) 0% ${libraryPercent.toFixed(2)}%,
      var(--color-ebook) ${libraryPercent.toFixed(2)}% ${(
        libraryPercent + ebookPercent
      ).toFixed(2)}%,
      var(--color-package) ${(libraryPercent + ebookPercent).toFixed(2)}% 100%
    )`,
  };

  const chartLegendData = [
    {
      name: "Library",
      value: libraryRevenue,
      percent: libraryPercent,
      colorVar: "var(--color-library)",
    },
    {
      name: "E-Book",
      value: ebookRevenue,
      percent: ebookPercent,
      colorVar: "var(--color-ebook)",
    },
    {
      name: "Package",
      value: packageRevenue,
      percent: packagePercent,
      colorVar: "var(--color-package)",
    },
  ].sort((a, b) => b.value - a.value);

  const newSummaryCards = [
    {
      icon: faMoneyBillWave,
      title: "Total Revenue",
      value: formatCurrency(totalRevenue),
      color: "var(--color-total-revenue)",
    },
    {
      icon: faBookOpen,
      title: "Library Revenue",
      value: formatCurrency(libraryRevenue),
      color: "var(--color-library)",
    },
    {
      icon: faShoppingCart,
      title: "E-book & Package Revenue",
      value: formatCurrency(otherRevenue),
      color: "var(--color-ebook)",
    },
  ];

  const topSellingEbooks = dashboardCounts?.topSellingEbooks || [];
  const topSellingPackages = dashboardCounts?.topSellingPackages || [];
  const topBookedLibraries = dashboardCounts?.topBookedLibraries || [];
  console.log(dashboardCounts)
  return (
    <>
      <section className="main-sec">
        <div className="row">
          <div className="col-lg-12">
            <div className="dashboard-title">
              <h4 className="dash-head ">
                Welcome To Pidigihub
              </h4>
            </div>
          </div>
          <div className="col-lg-12 mb-4">
            <h5 className="section-title">Core Metrics</h5>
            <div className="summary-cards-grid">
              <div className="summary-card">
                <div className="summary-icon">
                  <FontAwesomeIcon icon={faUser} />
                </div>
                <div className="summary-content">
                  <h6>Total Users</h6>
                  <h4>{dashboardCounts?.users || 0}</h4>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <FontAwesomeIcon icon={faBookOpen} />
                </div>
                <div className="summary-content">
                  <h6>Total Libraries</h6>
                  <h4>{dashboardCounts?.libraries || 0}</h4>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="summary-content">
                  <h6>Total Mentors</h6>
                  <h4>{dashboardCounts?.mentors || 0}</h4>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <FontAwesomeIcon icon={faFileAlt} />
                </div>
                <div className="summary-content">
                  <h6>Total Ebooks</h6>
                  <h4>{dashboardCounts?.ebooks || 0}</h4>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <FontAwesomeIcon icon={faTags} />
                </div>
                <div className="summary-content">
                  <h6>Active Coupons</h6>
                  <h4>{dashboardCounts?.coupons || 0}</h4>
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-icon">
                  <FontAwesomeIcon icon={faBoxOpen} />
                </div>
                <div className="summary-content">
                  <h6>Total Packages</h6>
                  <h4>{dashboardCounts?.packages || 0}</h4>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-12 mb-4">
            <div className="date-filter-container">
              <div className="date-filter-header">
                <FontAwesomeIcon icon={faCalendar} className="me-2" />
                <span className="filter-label">Filter Data by Date Range (Default 7 Days):</span>
              </div>
              <div className="date-inputs-group">
                <div className="date-input-wrapper">
                  <label htmlFor="startDate">Start Date</label>
                  <input
                    type="date"
                    id="startDate"
                    className="form-control date-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="date-input-wrapper">
                  <label htmlFor="endDate">End Date</label>
                  <input
                    type="date"
                    id="endDate"
                    className="form-control date-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
                <button
                  className="btn btn-filter"
                  onClick={handleDateFilter}
                >
                  Apply Filter
                </button>
                {(startDate !== today || endDate !== today) && (
                  <button
                    className="btn btn-reset"
                    onClick={handleResetToToday}
                  >
                    Today
                  </button>
                )}
                {(startDate === today || endDate === today) && (
                  <button
                    className="btn btn-reset"
                    onClick={handleResetToDefault}
                  >
                    CLear Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Revenue Summary Cards */}
          <div className="col-lg-12">
            <h5 className="section-title">Revenue Snapshot</h5>
            <div className="summary-cards-grid revenue-cards-grid">
              {newSummaryCards.map((card, index) => (
                <div key={index} className="summary-card revenue-card">
                  <div
                    className="summary-icon"
                    style={{
                      backgroundColor: card.color,
                      opacity: 0.1,
                      color: '#fff',
                    }}
                  >
                    <FontAwesomeIcon icon={card.icon} />
                  </div>
                  <div className="summary-content">
                    <h6>{card.title}</h6>
                    <h4 style={{ color: card.color }}>{card.value}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Counts Summary Cards */}


          {/* Revenue Distribution Chart */}
          <div className="col-lg-12 mt-4">
            <div className="dashboard-chart-card">
              <div className="chart-header">
                <h6>Revenue Distribution by Source</h6>
              </div>
              <div className="chart-content">
                <div className="doughnut-chart-main-wrapper">
                  <div className="doughnut-chart-wrapper">
                    <div
                      className="doughnut-chart-inner"
                      style={pieChartStyle}
                      aria-label="Sales distribution chart"
                    >
                      <div className="chart-center-text">
                        <small className="text-muted">Total Revenue</small>
                        <h4>{formatCurrency(totalRevenue)}</h4>
                      </div>
                    </div>
                  </div>
                  <div className="doughnut-legend">
                    {chartLegendData.map((item) => (
                      <div key={item.name} className="legend-item">
                        <div className="legend-label-group">
                          <span
                            className="legend-color-dot"
                            style={{ background: item.colorVar }}
                          ></span>
                          <span className="legend-label">{item.name}</span>
                          <span className="legend-percent">
                            ({item.percent.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="legend-value">
                          {formatCurrency(item.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-12 mt-4">
            <h5 className="section-title">Top Performers Comparison</h5>

            {/* Top Selling Packages Chart */}
            <div className="comparison-chart-card mb-4">
              <div className="chart-header">
                <h6>🏆 Top Selling Packages</h6>
              </div>
              <div className="chart-content">
                {topSellingPackages.length > 0 ? (
                  <div className="bar-chart-container">
                    {topSellingPackages.slice(0, 5).map((pkg, index) => {
                      const maxSales = Math.max(...topSellingPackages.map(p => p.totalSales));
                      const percentage = (pkg.totalSales / maxSales) * 100;
                      return (
                        <div key={index} className="bar-chart-row">
                          <div className="bar-label">
                            <div className="bar-rank">#{index + 1}</div>
                            <div className="bar-info">
                              <p className="bar-title">{pkg.title}</p>
                              <small className="bar-code">{pkg.packageCode}</small>
                            </div>
                          </div>
                          <div className="bar-track">
                            <div
                              className="bar-fill package-bar"
                              style={{ width: `${percentage}%` }}
                            >
                              <span className="bar-value">{pkg.totalSales} sales</span>
                            </div>
                          </div>
                          <div className="bar-price">{formatCurrency(pkg.price)}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="empty-state">No package sales data available for the selected period</p>
                )}
              </div>
            </div>

            {/* Top Selling Ebooks Chart */}
            <div className="comparison-chart-card mb-4">
              <div className="chart-header">
                <h6>📚 Top Selling E-books</h6>
              </div>
              <div className="chart-content">
                {topSellingEbooks.length > 0 ? (
                  <div className="bar-chart-container">
                    {topSellingEbooks.slice(0, 5).map((ebook, index) => {
                      const maxSales = Math.max(...topSellingEbooks.map(e => e.totalSales));
                      const percentage = (ebook.totalSales / maxSales) * 100;
                      return (
                        <div key={index} className="bar-chart-row">
                          <div className="bar-label">
                            <div className="bar-rank">#{index + 1}</div>
                            <div className="bar-info">
                              <p className="bar-title">{ebook.bookTitle || ebook.title}</p>
                              <small className="bar-code">{ebook.ebookCode}</small>
                            </div>
                          </div>
                          <div className="bar-track">
                            <div
                              className="bar-fill ebook-bar"
                              style={{ width: `${percentage}%` }}
                            >
                              <span className="bar-value">{ebook.totalSales} sales</span>
                            </div>
                          </div>
                          <div className="bar-price">{formatCurrency(ebook.price)}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="empty-state">No e-book sales data available for the selected period</p>
                )}
              </div>
            </div>

            {/* Top Booked Libraries Chart */}
            <div className="comparison-chart-card">
              <div className="chart-header">
                <h6>🏢 Top Booked Libraries</h6>
              </div>
              <div className="chart-content">
                {topBookedLibraries.length > 0 ? (
                  <div className="bar-chart-container">
                    {topBookedLibraries.slice(0, 5).map((library, index) => {
                      const maxBookings = Math.max(...topBookedLibraries.map(l => l.totalBookings));
                      const percentage = (library.totalBookings / maxBookings) * 100;
                      return (
                        <div key={index} className="bar-chart-row">
                          <div className="bar-label">
                            <div className="bar-rank">#{index + 1}</div>
                            <div className="bar-info">
                              <p className="bar-title">{library.libraryName}</p>
                              <small className="bar-code">{library.libraryCode} • {library.city}</small>
                            </div>
                          </div>
                          <div className="bar-track">
                            <div
                              className="bar-fill library-bar"
                              style={{ width: `${percentage}%` }}
                            >
                              <span className="bar-value">{library.totalBookings} bookings</span>
                            </div>
                          </div>
                          <div className="bar-category">
                            <span className={`category-badge ${library.category}`}>
                              {library.category}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="empty-state">No library booking data available for the selected period</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Activity Section */}
          <div className="col-lg-12 mt-4">
            <h5 className="section-title">Recent Activity</h5>
            <div className="row">
              <div className="col-lg-6 col-md-12 mb-4">
                <div className="recent-activity-card">
                  <h6>
                    New Users ({dashboardCounts?.recentUser?.length || 0})
                  </h6>
                  <div className="activity-list">
                    {dashboardCounts?.recentUser?.slice(0, 3).map((user) => (
                      <div key={user._id || user.id} className="activity-item">
                        <FontAwesomeIcon
                          icon={faSignInAlt}
                          className="activity-icon-sm"
                        />
                        <div>
                          <p className="activity-name">
                            {user.name || "New User"}
                          </p>
                          <small className="activity-detail">
                            {user.email} - Joined:{" "}
                            {new Date(user.createdAt).toLocaleDateString()}
                          </small>
                        </div>
                      </div>
                    ))}
                    {(dashboardCounts?.recentUser?.length === 0 ||
                      !dashboardCounts?.recentUser) && (
                        <p className="text-muted text-center mt-3">
                          No recent users found.
                        </p>
                      )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6 col-md-12 mb-4">
                <div className="recent-activity-card">
                  <h6>
                    Recent Purchases (
                    {(dashboardCounts?.RecentE_bookPurchases?.length || 0) +
                      (dashboardCounts?.recentMockTestPurchases?.length || 0)}
                    )
                  </h6>
                  <div className="activity-list">
                    {[
                      ...(dashboardCounts?.RecentE_bookPurchases || []),
                      ...(dashboardCounts?.recentMockTestPurchases || []),
                    ]
                      .sort(
                        (a, b) =>
                          new Date(b.purchaseDate || b.createdAt) -
                          new Date(a.purchaseDate || a.createdAt)
                      )
                      .slice(0, 3)
                      .map((purchase, index) => (
                        <div key={index} className="activity-item">
                          <FontAwesomeIcon
                            icon={faShoppingCart}
                            className="activity-icon-sm"
                          />
                          <div>
                            <p className="activity-name">
                              {purchase.ebookId?.bookTitle ||
                                purchase.packageId?.title ||
                                "Purchase"}
                            </p>
                            <small className="activity-detail">
                              User: {purchase.userId?.name || "N/A"} -{" "}
                              {formatCurrency(purchase.amount)} (
                              {purchase.paymentStatus})
                            </small>
                          </div>
                        </div>
                      ))}
                    {(dashboardCounts?.RecentE_bookPurchases?.length === 0 ||
                      !dashboardCounts?.RecentE_bookPurchases) &&
                      (dashboardCounts?.recentMockTestPurchases?.length === 0 ||
                        !dashboardCounts?.recentMockTestPurchases) && (
                        <p className="text-muted text-center mt-3">
                          No recent purchases found.
                        </p>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>
        {`
          :root {
            --primary-color: #26a084;
            --primary-light: #e0fff9;
            --bg-color: #f8fafc;
            --card-bg: #ffffff;
            --text-color: #1e293b;
            --text-light: #64748b;
            --border-color: #e2e8f0;
            --shadow: 0 4px 16px rgba(0,0,0,0.05);
            --color-library: #60a5fa;
            --color-ebook: #34d399;
            --color-package: #fcd34d;
            --color-total-revenue: #ef4444;
          }

          .main-sec {
            background-color: var(--bg-color);
            padding: 2rem;
          }

          .dash-head {
            font-size: 1.8rem;
            font-weight: 800;
            color: var(--text-color);
            margin-bottom: 2rem;
            border-bottom: 3px solid var(--primary-color);
            display: inline-block;
            padding-bottom: 0.5rem;
          }
          
          .section-title {
            font-size: 1.25rem;
            font-weight: 700;
            color: var(--text-color);
            margin-bottom: 1rem;
            padding-left: 0.5rem;
            border-left: 4px solid var(--primary-color);
          }

          /* New Date Range Filter Styles */
          .date-filter-container {
            background: var(--card-bg);
            border-radius: 12px;
            padding: 1.5rem;
            box-shadow: var(--shadow);
            border: 1px solid var(--border-color);
          }

          .date-filter-header {
            display: flex;
            align-items: center;
            font-weight: 600;
            color: var(--text-color);
            font-size: 1rem;
            margin-bottom: 1rem;
          }

          .date-inputs-group {
            display: flex;
            gap: 1rem;
            align-items: flex-end;
            flex-wrap: wrap;
          }

          .date-input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            min-width: 180px;
          }

          .date-input-wrapper label {
            font-size: 0.875rem;
            font-weight: 600;
            color: var(--text-light);
            margin: 0;
          }

          .date-input {
            border: 1px solid var(--border-color);
            border-radius: 8px;
            padding: 0.6rem 1rem;
            font-size: 0.95rem;
            font-weight: 500;
            color: var(--text-color);
            transition: all 0.3s ease;
          }

          .date-input:focus {
            border-color: var(--primary-color);
            box-shadow: 0 0 0 3px rgba(38, 160, 132, 0.1);
            outline: none;
          }

          .btn-filter, .btn-reset {
            padding: 0.6rem 1.5rem;
            border-radius: 8px;
            font-weight: 600;
            font-size: 0.95rem;
            border: none;
            cursor: pointer;
            transition: all 0.3s ease;
          }

          .btn-filter {
            background: var(--primary-color);
            color: white;
          }

          .btn-filter:hover {
            background: #1f8a70;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(38, 160, 132, 0.3);
          }

          .btn-reset {
            background: var(--text-light);
            color: white;
          }

          .btn-reset:hover {
            background: #475569;
            transform: translateY(-2px);
          }

          .summary-cards-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1.5rem;
          }
          
          .revenue-cards-grid {
              grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
              margin-bottom: 1.5rem;
          }

          .summary-card {
            background: var(--card-bg);
            color: var(--text-color);
            padding: 1.5rem;
            border-radius: 12px;
            display: flex;
            align-items: center;
            gap: 1.25rem;
            border: 1px solid var(--border-color);
            box-shadow: var(--shadow);
            transition: all 0.2s ease;
          }

          .summary-card:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 20px rgba(0,0,0,0.08);
          }

          .summary-icon {
            background: var(--primary-light);
            color: var(--primary-color);
            width: 55px;
            height: 55px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            flex-shrink: 0;
            transition: background 0.3s ease;
          }
          
          .summary-card:hover .summary-icon {
            background: var(--primary-color);
            color: var(--card-bg);
          }
          
          .revenue-card .summary-icon {
              background: none;
          }

          .summary-content h6 {
            margin: 0;
            font-size: 0.875rem;
            color: var(--text-light);
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .summary-content h4 {
            margin: 0.25rem 0 0 0;
            font-size: 2rem;
            font-weight: 800;
            color: var(--text-color);
          }
          
          .revenue-card .summary-content h4 {
              font-size: 1.75rem;
          }

          .dashboard-chart-card, .comparison-chart-card {
            background: var(--card-bg);
            border-radius: 12px;
            box-shadow: var(--shadow);
            border: 1px solid var(--border-color);
            overflow: hidden;
          }

          .chart-header {
            padding: 1.5rem;
            border-bottom: 1px solid var(--border-color);
          }
          
          .chart-header h6 {
            font-weight: 700;
            font-size: 1.25rem;
            color: var(--text-color);
            margin: 0;
          }
          
          .chart-content {
            padding: 2rem;
          }

          .doughnut-chart-main-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4rem;
            flex-wrap: wrap;
          }

          .doughnut-chart-wrapper {
            width: 280px;  
            height: 280px;
            position: relative;
            flex-shrink: 0;
            box-shadow: 0 0 0 10px rgba(0,0,0,0.01);  
            border-radius: 50%;
          }

          .doughnut-chart-inner {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            position: relative;
          }

          .chart-center-text {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 65%;
            height: 65%;
            background: var(--card-bg);
            border-radius: 50%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 1rem;
            text-align: center;
            box-shadow: 0 0 0 10px var(--bg-color);
          }
          
          .chart-center-text small {
            font-size: 0.9rem;
            color: var(--text-light);
            font-weight: 500;
            margin-bottom: 0.25rem;
          }

          .chart-center-text h4 {
            font-size: 1.5rem;
            font-weight: 800;
            color: var(--color-total-revenue);
            margin: 0;
          }
          
          .doughnut-legend {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            flex-grow: 1;
            max-width: 350px;
            min-width: 250px;
          }

          .legend-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.75rem 1rem;
            background: var(--bg-color);
            border-radius: 8px;
            transition: all 0.2s ease;
          }
          
          .legend-item:hover {
            background: var(--primary-light);
          }

          .legend-label-group {
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .legend-color-dot {
            width: 14px;
            height: 14px;
            border-radius: 4px;
            flex-shrink: 0;
          }

          .legend-label {
            font-weight: 600;
            color: var(--text-color);
            font-size: 1rem;
          }

          .legend-percent {
            font-weight: 500;
            color: var(--text-light);
            font-size: 0.9rem;
            margin-left: 0.25rem;
          }

          .legend-value {
            font-weight: 700;
            color: var(--primary-color);
            font-size: 1rem;
          }

          /* Modern Bar Chart Styles */
          .bar-chart-container {
            display: flex;
            flex-direction: column;
            gap: 1.25rem;
          }

          .bar-chart-row {
            display: grid;
            grid-template-columns: minmax(200px, 1fr) 3fr auto;
            gap: 1rem;
            align-items: center;
            padding: 1rem;
            background: var(--bg-color);
            border-radius: 10px;
            transition: all 0.3s ease;
          }

          .bar-chart-row:hover {
            background: var(--primary-light);
            transform: translateX(5px);
          }

          .bar-label {
            display: flex;
            align-items: center;
            gap: 0.75rem;
          }

          .bar-rank {
            background: var(--primary-color);
            color: white;
            width: 32px;
            height: 32px;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            font-size: 0.9rem;
            flex-shrink: 0;
          }

          .bar-info {
            flex: 1;
            min-width: 0;
          }

          .bar-title {
            font-weight: 600;
            color: var(--text-color);
            font-size: 0.95rem;
            margin: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .bar-code {
            color: var(--text-light);
            font-size: 0.8rem;
          }

          .bar-track {
            position: relative;
            height: 40px;
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
          }

          .bar-fill {
            height: 100%;
            border-radius: 8px;
            display: flex;
            align-items: center;
            padding: 0 1rem;
            transition: width 1s ease;
            position: relative;
            overflow: hidden;
          }

          .package-bar {
            background: linear-gradient(135deg, #fcd34d 0%, #f59e0b 100%);
          }

          .ebook-bar {
            background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
          }

          .library-bar {
            background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
          }

          .bar-value {
            font-weight: 700;
            color: white;
            font-size: 0.85rem;
            white-space: nowrap;
            text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          }

          .bar-price {
            font-weight: 700;
            color: var(--primary-color);
            font-size: 1rem;
            white-space: nowrap;
            min-width: 80px;
            text-align: right;
          }

          .bar-category {
            min-width: 100px;
            text-align: right;
          }

          .category-badge {
            display: inline-block;
            padding: 0.35rem 0.75rem;
            border-radius: 20px;
            font-size: 0.75rem;
            font-weight: 600;
            text-transform: capitalize;
          }

          .category-badge.premium {
            background: #fef3c7;
            color: #92400e;
          }

          .category-badge.standard {
            background: #dbeafe;
            color: #1e40af;
          }

          .empty-state {
            text-align: center;
            padding: 3rem 1rem;
            color: var(--text-light);
            font-size: 1rem;
          }

          .recent-activity-card {
            background: var(--card-bg);
            padding: 1.5rem;
            border-radius: 12px;
            box-shadow: var(--shadow);
            border: 1px solid var(--border-color);
            min-height: 250px;
          }
          
          .recent-activity-card h6 {
            font-weight: 700;
            font-size: 1.1rem;
            color: var(--text-color);
            margin-bottom: 1rem;
            padding-bottom: 0.5rem;
            border-bottom: 1px solid var(--border-color);
          }
          
          .activity-list {
            display: flex;
            flex-direction: column;
            gap: 1rem;
          }
          
          .activity-item {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 0.5rem 0;
            border-bottom: 1px dashed var(--border-color);
          }
          
          .activity-item:last-child {
            border-bottom: none;
          }
          
          .activity-icon-sm {
            color: var(--primary-color);
            font-size: 1.1rem;
            flex-shrink: 0;
          }
          
          .activity-name {
            font-weight: 600;
            color: var(--text-color);
            margin: 0;
            font-size: 0.95rem;
          }
          
          .activity-detail {
            color: var(--text-light);
            font-size: 0.85rem;
            display: block;
          }

          @media (max-width: 992px) {
            .revenue-cards-grid {
                grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            }
            .bar-chart-row {
                grid-template-columns: 1fr;
                gap: 0.75rem;
            }
            .bar-track {
                order: 3;
            }
          }
          
          @media (max-width: 768px) {
            .main-sec {
              padding: 1rem;
            }
            .date-inputs-group {
                flex-direction: column;
                align-items: stretch;
            }
            .date-input-wrapper {
                min-width: 100%;
            }
            .btn-filter, .btn-reset {
                width: 100%;
            }
            .doughnut-chart-main-wrapper {
                flex-direction: column;
                gap: 2rem;
            }
            .doughnut-chart-wrapper {
                width: 200px;  
                height: 200px;
            }
            .chart-content {
              padding: 1.5rem;
            }
            .doughnut-legend {
                width: 100%;
                max-width: 100%;
            }
            .summary-cards-grid {
                gap: 1rem;
            }
            .summary-card {
                padding: 1rem;
                gap: 0.75rem;
            }
            .summary-content h4 {
                font-size: 1.5rem;
            }
            .revenue-card .summary-content h4 {
                font-size: 1.5rem;
            }
          }
        `}
      </style>
    </>
  );
}
